<script setup>
import Toggle from '@vueform/toggle';
import List from '@c/Accounting/List.vue';
import AddAccounting from '@c/Accounting/AddAccounting.vue';
import AddProject from '@c/Accounting/AddProject.vue';
import { onMounted, ref, watch } from 'vue';
import ProjectNav from '@c/Accounting/ProjectNav';
import { ACCOUNTING, PROJECT, ME } from '@/api';
import SearchNav from './SearchNav';
import AccountingFooter from './AccountingFooter';
import { UNIT } from '../api';

const year = ref(dayjs().year());
const units = ref([]);
const month = ref(dayjs().month() + 1);
const projectName = ref(null);
const status = ref('waiting');
const isProject = ref(false);
const projectId = ref(null);
const projects = ref([]);
const project = ref({});
const accountings = ref([]);
const accounting = ref({});
const showModal = ref(false);
const showAccounting = ref(false);
const canAddItem = ref(true);
const proNav = ref(null);
const projectType = ref(null);
const accountingCompanies = ref([]);
const addAccountingRef = ref(null);

async function closeModal() {
  showModal.value = false;
  accounting.value = {};
}

async function closeAccountingModal() {
  // 檢查是否正在上傳中
  if (addAccountingRef.value?.isSubmitting) {
    return;
  }
  showAccounting.value = false;
  accounting.value = {};
  if (projectId.value) {
    await getAccountingByProject(projectId.value);
  } else {
    await getAccountings();
  }
}

const setMonth = async value => {
  month.value = value;
  await getAccountings();
};

const setStatus = async value => {
  status.value = value;
  await getAccountings();
};

const setProjectName = async value => {
  projectName.value = value;
  await getAccountings();
};

function addProject() {
  showModal.value = true;
}

async function editProject(id) {
  const { data } = await PROJECT.get(id, { include: 'accountings', type: 'normal' });
  project.value = data.data;
  showModal.value = true;
}

const refresh = async type => {
  if (type === 'project') {
    await getAccountingByProject(projectId.value);
  } else {
    await getAccountings();
  }
};

const getAccountingByProject = async id => {
  projectId.value = id;
  projectType.value = projects.value.find(item => item.id === id)?.type;
  const canAddStatus = ['created', 'update'];
  if (id) {
    const { data } = await PROJECT.get(id, {
      include: 'accountings',
      type: projectType.value,
    });
    accountings.value = data.data.accountings;
    canAddItem.value = canAddStatus.includes(data.data.status);
  } else {
    canAddItem.value = false;
  }
};

const refreshProjectAction = async id => {
  if (id) {
    await getAccountingByProject(id);
  } else {
    await getProjects();
  }
};

const readAccounting = async id => {
  const { data } = await ACCOUNTING.get(id);
  accounting.value = data.data;
  showAccounting.value = true;
};

const getAccountings = async () => {
  const { data } = await ACCOUNTING.index({
    year: year.value,
    month: month.value,
    name: projectName.value,
    status: status.value,
  });
  accountings.value = data.data;
};

const getUnits = async () => {
  const { data } = await UNIT.index();
  units.value = data.data;
};

const refreshAccounting = async () => {
  proNav.value.setInitial();
  await getAccountings();
};

const exportExcel = async () => {
  await ACCOUNTING.export({
    year: year.value,
    month: month.value,
    name: projectName.value,
    status: status.value,
    export: true,
    projectId: projectId.value,
  });
};

const addAccounting = async () => {
  accounting.value = {};
  showAccounting.value = true;
};

const getProjects = async () => {
  const { data } = await PROJECT.index({
    year: year.value,
  });
  projects.value = data.data;
  proNav.value.setInitial();
};

const getAssociateCompanies = async () => {
  const { data } = await ME.accountingCompanies();
  accountingCompanies.value = data.data;
};

watch(
  () => isProject.value,
  async value => {
    if (value) {
      accountings.value = [];
      await getProjects();
      canAddItem.value = false;
    } else {
      await refreshAccounting();
      canAddItem.value = true;
    }
  },
);

watch(
  () => year.value,
  async value => {
    await getAccountings();
  },
);

onMounted(async () => {
  await getAccountings();
  await getUnits();
  await getAssociateCompanies();
});
</script>

<template>
  <div class="px-2 py-4 rounded" :class="{ 'bg-gray-400': isProject }">
    <div class="grid items-center grid-cols-2">
      <div class="grid grid-cols-2 gap-2">
        <div class="w-1/3 text-lg font-bold" :class="{ 'text-white': isProject }">
          Project
        </div>
        <Toggle class="w-2/3" v-model="isProject" />
      </div>
      <div v-show="!isProject" class="flex items-center w-full">
        <button
          :disabled="isProject"
          class="w-1/6 py-1 text-center text-white bg-indigo-400 rounded"
          @click="year--"
        >
          ◀
        </button>
        <div
          class="w-2/3 text-lg font-bold text-center"
          :class="{ 'text-white': isProject }"
        >
          {{ year }}
        </div>
        <button
          class="w-1/6 py-1 text-center text-white bg-indigo-400 rounded"
          :disabled="isProject"
          @click="year++"
        >
          ▶
        </button>
      </div>
    </div>

    <ProjectNav
      ref="proNav"
      v-if="isProject"
      :projects="projects"
      :accountingCompanies="accountingCompanies"
      @addProject="addProject"
      @editProject="editProject"
      @selectProject="getAccountingByProject"
    />

    <SearchNav
      v-else
      @changeMonth="setMonth"
      @changeStatus="setStatus"
      @changeProjectName="setProjectName"
    />
  </div>
  <hr class="mt-4" />
  <List class="mt-2" @show="readAccounting" :accountings="accountings" />
  <AccountingFooter
    :canAddItem="canAddItem"
    @exportAccountings="exportExcel"
    @addAccounting="addAccounting"
  />
  <vue-final-modal
    classes="flex justify-center items-center w-full"
    v-model="showAccounting"
    :click-to-close="false"
    :esc-to-close="false"
  >
    <AddAccounting
      ref="addAccountingRef"
      :units="units"
      :accounting="accounting"
      :isProject="isProject"
      :projectId="projectId"
      :projectType="projectType"
      :accountingCompanies="accountingCompanies"
      @close="closeAccountingModal"
      @refresh="refresh"
    />
  </vue-final-modal>
  <vue-final-modal
    classes="flex justify-center items-center w-full"
    v-model="showModal"
  >
    <AddProject
      :project="project"
      @close="closeModal"
      @refresh="refreshProjectAction"
    />
  </vue-final-modal>
</template>
